import { chainOrSuspend, pyNone } from "@Sk";
import PyDefUtils from "PyDefUtils";
import { AnvilHookSpec, Component, ComponentConstructor } from "@runtime/components/Component";
import { designerApi } from "@runtime/runner/component-designer-api";
import { BASE_MODULES } from "../python-environment";

const { DesignGridPanel, GridPanel } = BASE_MODULES["anvil"];

const mkDesignerHookSpec = (oldHookSpec: AnvilHookSpec): Partial<AnvilHookSpec> => {
    return {
        enableDropMode(dragging) {
            const element = this.anvil$hooks.domElement!;
            return [
                {
                    element: element,
                    freePlacement: (dropping) => {
                        const baseline = element.getBoundingClientRect();
                        const wwil = dropping.map(({ x, y, width, height }) =>
                            this._anvil.whereWillItLand(
                                x + baseline.x,
                                y + baseline.y,
                                width ?? 200,
                                height ?? 10,
                                x,
                                y
                            )
                        );
                        const r = {
                            dropLocations: wwil.map(({ estimatedPosition: { x, y, width, height } }) => ({
                                x: x - (document.scrollingElement?.scrollLeft || 0) - baseline.x,
                                y: y - (document.scrollingElement?.scrollTop || 0) - baseline.y,
                                width,
                                height,
                            })),
                            dropInfo: {
                                minChildIdx: Math.min(...wwil.map((w) => w.childIdx)),
                                maxChildIdx: Math.max(...wwil.map((w) => w.childIdx)),
                                perComponentUpdates: wwil.map((w) => ({ layout_properties: w.layoutProperties })),
                            },
                        };
                        return r;
                    },
                },
            ];
        },
        disableDropMode: () => {},
        getContainerDesignInfo(forChild: Component) {
            const element = this.anvil$hooks.domElement!;
            const gridComponents = this._anvil.gridComponents as GridComponentInfo[];
            const thisComponent = gridComponents.find(({ pyComponent }) => pyComponent === forChild)!;
            const rowName = thisComponent?.row;
            const componentsInRow = gridComponents.filter(({ row }) => row === rowName);
            const colWidthPx = (element.clientWidth + 30) / 12;
            const childEl = forChild.anvil$hooks.domElement!;
            const layoutProperties = thisComponent?.layoutProperties;
            let currentColWidth = layoutProperties?.width_xs ?? 0;
            let originalWidth: number;

            const adjustContainerClasses = (newColWidth: number) => {
                thisComponent.layoutProperties.width_xs = newColWidth;
                const dummyRow = { lastCol: {} };
                for (const component of componentsInRow) {
                    const classList = this._anvil.getContainerClassList(dummyRow, component);
                    const el = component.pyComponent.anvil$hooks.domElement!;
                    el.parentElement!.className = classList.join(" ");
                }
            };

            return {
                // OLD version didn't expose col_xs, width_xs in the designer
                layoutPropertyDescriptions: [],
                interactions: [
                    {
                        type: "handle",
                        position: "right",
                        direction: "x",
                        callbacks: {
                            grab: () => {
                                originalWidth = childEl.clientWidth;
                            },
                            drag: (dx, dy) => {
                                const newWidthPx = dx + originalWidth;
                                const totalCols = Math.round(newWidthPx / colWidthPx);
                                const newColWidth = Math.min(Math.max(1, totalCols), 12);
                                if (newColWidth !== currentColWidth) {
                                    adjustContainerClasses(newColWidth);
                                    currentColWidth = newColWidth;
                                }
                            },
                            drop: (dx, dy) => {
                                designerApi.updateComponentProperties(forChild, {}, { width_xs: currentColWidth });
                                designerApi.notifyInteractionsChanged(forChild);
                            },
                        },
                    },
                ],
            };
        },
    };
};

interface GridComponentInfo {
    row: string;
    lastCol: { xs: number };
    pyComponent: Component;
    layoutProperties: { width_xs: number; col_xs: number };
}

export const DesignGridPanelV3 = Sk.misceval.buildClass(
    BASE_MODULES["anvil"],
    ($gbl, $loc) => {
        const oldHookSpec = GridPanel.prototype.anvil$hookSpec;
        $loc.anvil$hookSpec = Object.assign(Object.create(oldHookSpec), mkDesignerHookSpec(oldHookSpec));
    },
    "DesignGridPanelV3",
    [DesignGridPanel]
) as ComponentConstructor;
